import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  FileCheck, Camera, Upload, CheckCircle2, AlertCircle, 
  Building2, DollarSign, FileText, Loader2, X, Plus,
  Shield, ClipboardCheck, PenTool, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

function ContractorCompliancePortal() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const phaseId = searchParams.get('phase');
  
  const [isLoading, setIsLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [error, setError] = useState(null);
  
  // E-Signature state
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState(null);
  const [lienWaiverData, setLienWaiverData] = useState({
    claimant_name: '',
    claimant_address: '',
    waiver_amount: '',
    through_date: new Date().toISOString().split('T')[0],
    work_description: ''
  });
  
  // Upload states
  const [lienWaiverFile, setLienWaiverFile] = useState(null);
  const [completionPhotos, setCompletionPhotos] = useState([]);
  const [photoDescription, setPhotoDescription] = useState('');
  const [inspectionFile, setInspectionFile] = useState(null);
  const [w9File, setW9File] = useState(null);
  const [coiFile, setCoiFile] = useState(null);
  const [coiExpiration, setCoiExpiration] = useState('');
  const [signatureMethod, setSignatureMethod] = useState('esign'); // 'esign' or 'upload'
  
  const [isUploading, setIsUploading] = useState({
    lienWaiver: false,
    photos: false,
    inspection: false,
    w9: false,
    coi: false
  });

  useEffect(() => {
    if (token) {
      loadPortalData();
    } else {
      setError('Invalid portal link. Please use the link provided in your email.');
      setIsLoading(false);
    }
  }, [token, phaseId]);

  const loadPortalData = async () => {
    try {
      setIsLoading(true);
      const res = await apiService.getContractorPortalData(token, phaseId);
      setPortalData(res.data);
      
      // Pre-fill lien waiver data
      const { phase, contractor, project } = res.data;
      if (phase || contractor) {
        setLienWaiverData({
          claimant_name: contractor?.company || contractor?.name || '',
          claimant_address: '',
          waiver_amount: phase?.lien_waiver_amount || phase?.amount || '',
          through_date: new Date().toISOString().split('T')[0],
          work_description: phase?.name || ''
        });
      }
    } catch (err) {
      console.error('Failed to load portal data:', err);
      setError('This portal link is invalid or has expired. Please contact the project owner for a new link.');
    } finally {
      setIsLoading(false);
    }
  };

  // Signature canvas functions - improved for smoother signatures
  const getCanvasPoint = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const point = getCanvasPoint(e, canvas);
    const ctx = canvas.getContext('2d');
    
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    setLastPoint(point);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const point = getCanvasPoint(e, canvas);
    
    // Smooth line drawing with quadratic curves
    if (lastPoint) {
      const midPoint = {
        x: (lastPoint.x + point.x) / 2,
        y: (lastPoint.y + point.y) / 2
      };
      
      ctx.quadraticCurveTo(lastPoint.x, lastPoint.y, midPoint.x, midPoint.y);
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(midPoint.x, midPoint.y);
    }
    
    setLastPoint(point);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.stroke();
      }
    }
    setIsDrawing(false);
    setLastPoint(null);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const submitESignature = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const signatureData = canvas.toDataURL('image/png');
    
    // Check if signature is empty
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasContent = imageData.data.some((pixel, i) => i % 4 === 3 && pixel > 0);
    
    if (!hasContent) {
      toast.error('Please sign before submitting');
      return;
    }
    
    setIsUploading(prev => ({ ...prev, lienWaiver: true }));
    try {
      await apiService.submitComplianceESignature(token, phaseId, signatureData, lienWaiverData);
      toast.success('Lien waiver signed successfully!', {
        description: 'Your e-signature has been recorded.',
        duration: 5000
      });
      
      // Reload data and check if all requirements are now complete
      const res = await apiService.getContractorPortalData(token, phaseId);
      setPortalData(res.data);
      
      // Check if all requirements are now fulfilled
      const phase = res.data.phase;
      const allComplete = phase?.lien_waiver_signed && 
                          phase?.completion_photos_uploaded && 
                          (!phase?.requires_inspection || phase?.inspection_passed);
      
      if (allComplete) {
        setTimeout(() => {
          toast.success('All Requirements Complete!', {
            description: 'Thank you! Payment can now be processed by the project owner.',
            duration: 8000
          });
        }, 1000);
      }
    } catch (err) {
      console.error('Signature error:', err);
      toast.error('Failed to submit signature. Please try again.');
    } finally {
      setIsUploading(prev => ({ ...prev, lienWaiver: false }));
    }
  };

  const handleLienWaiverUpload = async () => {
    if (!lienWaiverFile) {
      toast.error('Please select a lien waiver file');
      return;
    }
    
    setIsUploading(prev => ({ ...prev, lienWaiver: true }));
    try {
      const formData = new FormData();
      formData.append('file', lienWaiverFile);
      formData.append('phase_id', phaseId);
      
      await apiService.uploadPortalLienWaiver(token, formData);
      toast.success('Lien waiver uploaded successfully!');
      setLienWaiverFile(null);
      loadPortalData();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload lien waiver');
    } finally {
      setIsUploading(prev => ({ ...prev, lienWaiver: false }));
    }
  };

  const handlePhotosUpload = async () => {
    if (completionPhotos.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }
    
    setIsUploading(prev => ({ ...prev, photos: true }));
    try {
      const formData = new FormData();
      completionPhotos.forEach(photo => {
        formData.append('files', photo);
      });
      formData.append('phase_id', phaseId);
      formData.append('description', photoDescription);
      
      await apiService.uploadCompletionPhotos(token, formData);
      toast.success(`${completionPhotos.length} photo(s) uploaded successfully!`, {
        description: 'Completion photos have been submitted.',
        duration: 5000
      });
      setCompletionPhotos([]);
      setPhotoDescription('');
      
      // Reload data and check if all requirements are now complete
      const res = await apiService.getContractorPortalData(token, phaseId);
      setPortalData(res.data);
      
      // Check if all requirements are now fulfilled
      const phase = res.data.phase;
      const allComplete = phase?.lien_waiver_signed && 
                          phase?.completion_photos_uploaded && 
                          (!phase?.requires_inspection || phase?.inspection_passed);
      
      if (allComplete) {
        setTimeout(() => {
          toast.success('All Requirements Complete!', {
            description: 'Thank you! Payment can now be processed by the project owner.',
            duration: 8000
          });
        }, 1000);
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload photos');
    } finally {
      setIsUploading(prev => ({ ...prev, photos: false }));
    }
  };

  const handleInspectionUpload = async () => {
    if (!inspectionFile) {
      toast.error('Please select an inspection report');
      return;
    }
    
    setIsUploading(prev => ({ ...prev, inspection: true }));
    try {
      const formData = new FormData();
      formData.append('file', inspectionFile);
      formData.append('phase_id', phaseId);
      
      await apiService.uploadInspectionReport(token, formData);
      toast.success('Inspection report uploaded successfully!', {
        description: 'Your inspection documentation has been submitted.',
        duration: 5000
      });
      setInspectionFile(null);
      
      // Reload data and check if all requirements are now complete
      const res = await apiService.getContractorPortalData(token, phaseId);
      setPortalData(res.data);
      
      // Check if all requirements are now fulfilled
      const phase = res.data.phase;
      const allComplete = phase?.lien_waiver_signed && 
                          phase?.completion_photos_uploaded && 
                          (!phase?.requires_inspection || phase?.inspection_passed);
      
      if (allComplete) {
        setTimeout(() => {
          toast.success('All Requirements Complete!', {
            description: 'Thank you! Payment can now be processed by the project owner.',
            duration: 8000
          });
        }, 1000);
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload inspection report');
    } finally {
      setIsUploading(prev => ({ ...prev, inspection: false }));
    }
  };

  // Handle W-9 upload
  const handleW9Upload = async () => {
    if (!w9File) {
      toast.error('Please select a W-9 file to upload');
      return;
    }
    
    setIsUploading(prev => ({ ...prev, w9: true }));
    try {
      const formData = new FormData();
      formData.append('file', w9File);
      
      // Upload W-9 to contractor
      const contractorId = portalData?.contractor?.id;
      if (contractorId) {
        await apiService.uploadContractorW9(contractorId, formData);
        toast.success('W-9 uploaded successfully!', {
          description: 'Your W-9 form has been submitted.',
          duration: 5000
        });
        setW9File(null);
        
        // Reload data
        const res = await apiService.getContractorPortalData(token, phaseId);
        setPortalData(res.data);
      }
    } catch (err) {
      console.error('W-9 upload error:', err);
      toast.error('Failed to upload W-9');
    } finally {
      setIsUploading(prev => ({ ...prev, w9: false }));
    }
  };

  // Handle COI upload
  const handleCOIUpload = async () => {
    if (!coiFile) {
      toast.error('Please select a COI file to upload');
      return;
    }
    
    setIsUploading(prev => ({ ...prev, coi: true }));
    try {
      // v2.4: COI expiration is now MANDATORY (backend enforces too)
      if (!coiExpiration) {
        toast.error('Please enter the COI expiration date before uploading.');
        return;
      }
      const formData = new FormData();
      formData.append('file', coiFile);
      formData.append('expiration_date', coiExpiration);
      
      // Upload COI to contractor
      const contractorId = portalData?.contractor?.id;
      if (contractorId) {
        await apiService.uploadContractorCOI(contractorId, formData);
        toast.success('Certificate of Insurance uploaded successfully!', {
          description: 'Your COI has been submitted.',
          duration: 5000
        });
        setCoiFile(null);
        setCoiExpiration('');
        
        // Reload data
        const res = await apiService.getContractorPortalData(token, phaseId);
        setPortalData(res.data);
      }
    } catch (err) {
      console.error('COI upload error:', err);
      toast.error('Failed to upload Certificate of Insurance');
    } finally {
      setIsUploading(prev => ({ ...prev, coi: false }));
    }
  };

  const downloadLienWaiverPdf = async () => {
    try {
      toast.info('Generating lien waiver PDF...');
      const response = await apiService.downloadComplianceLienWaiverPdf(token, phaseId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const contractorName = portalData?.contractor?.company || portalData?.contractor?.name || 'contractor';
      link.setAttribute('download', `Lien_Waiver_${contractorName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Lien waiver downloaded!');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download lien waiver');
    }
  };

  const calculateProgress = () => {
    if (!portalData?.phase) return 0;
    const phase = portalData.phase;
    const contractor = portalData?.contractor;
    let completed = 0;
    let total = 0;
    
    // Lien waiver (always required for labor)
    total++;
    if (phase.lien_waiver_signed) completed++;
    
    // Completion photos (always required)
    total++;
    if (phase.completion_photos_uploaded) completed++;
    
    // Inspection (only if required)
    if (phase.requires_inspection) {
      total++;
      if (phase.inspection_passed) completed++;
    }
    
    // W-9 (always required)
    total++;
    if (contractor?.w9_received) completed++;
    
    // COI (always required)
    total++;
    if (contractor?.coi_received && !contractor?.coi_expired) completed++;
    
    return Math.round((completed / total) * 100);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Portal Access Error</h2>
              <p className="text-slate-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { phase, project, contractor } = portalData;
  const progress = calculateProgress();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-slate-800">Contractor Compliance Portal</h1>
          </div>
          <p className="text-slate-600">Complete the required documents to enable payment processing</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Project & Phase Info */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Project</p>
                <p className="font-semibold text-slate-800 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-500" />
                  {project?.name || 'N/A'}
                </p>
                {project?.address && (
                  <p className="text-sm text-slate-500 mt-1">{project.address}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Phase</p>
                <p className="font-semibold text-slate-800">{phase?.name || 'N/A'}</p>
                {contractor && (
                  <p className="text-sm text-slate-500 mt-1">{contractor.company || contractor.name}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Amount</p>
                <p className="font-semibold text-green-600 flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  {(phase?.lien_waiver_amount || phase?.amount || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Success Banner - Shown when all requirements are fulfilled */}
        {progress === 100 && (
          <div className="mb-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold mb-1">All Requirements Fulfilled!</h2>
                <p className="text-green-100 mb-3">
                  Thank you for completing all compliance requirements. Your documents have been submitted successfully 
                  and payment can now be processed by the project owner.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-white/20 text-white border-0">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Lien Waiver Signed
                  </Badge>
                  <Badge className="bg-white/20 text-white border-0">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Photos Uploaded
                  </Badge>
                  {phase?.requires_inspection && phase?.inspection_passed && (
                    <Badge className="bg-white/20 text-white border-0">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Inspection Passed
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Compliance Progress</span>
              <span className={`text-sm font-bold ${progress === 100 ? 'text-green-600' : 'text-blue-600'}`}>{progress}%</span>
            </div>
            <Progress value={progress} className={`h-3 ${progress === 100 ? '[&>div]:bg-green-500' : ''}`} />
            {progress === 100 ? (
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                All requirements completed! Payment can now be processed.
              </p>
            ) : (
              <p className="text-sm text-slate-500 mt-2">
                Complete all required items below to enable payment processing.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Compliance Items */}
        <div className="space-y-6">
          {/* 1. Lien Waiver */}
          <Card className={phase?.lien_waiver_signed ? 'border-green-200 bg-green-50/50' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    phase?.lien_waiver_signed ? 'bg-green-100' : 'bg-blue-100'
                  }`}>
                    {phase?.lien_waiver_signed ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <FileCheck className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg">Lien Waiver</CardTitle>
                    <CardDescription>
                      {phase?.lien_waiver_signed 
                        ? 'Signed and submitted' 
                        : `Sign lien waiver for $${(phase?.lien_waiver_amount || phase?.amount || 0).toLocaleString()}`
                      }
                    </CardDescription>
                  </div>
                </div>
                {phase?.lien_waiver_signed ? (
                  <Badge className="bg-green-100 text-green-700">Complete</Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">Required</Badge>
                )}
              </div>
            </CardHeader>
            {!phase?.lien_waiver_signed && (
              <CardContent>
                <div className="space-y-4">
                  {/* Waiver Text */}
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <h4 className="font-bold text-center text-lg mb-4">WAIVER OF LIEN</h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      FOR AND IN CONSIDERATION OF THE SUM LISTED BELOW or other valuable 
                      consideration, receipt of which is hereby acknowledged, the undersigned hereby waives 
                      and releases any and all liens, claims or rights of liens upon the premises described 
                      below, which the undersigned may have under the statutes of the State of Iowa relating to 
                      mechanics liens or for any other reason whatsoever on account of labor or materials or 
                      both, furnished or which may be furnished by the undersigned upon the said premises 
                      described below or any building or construction thereon.
                    </p>
                  </div>
                  
                  {/* Project Info (Read Only) */}
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-2 text-sm">Property Information</h5>
                    <div className="grid grid-cols-1 gap-1 text-sm text-blue-800">
                      <p><span className="font-medium">Name of Owner:</span> Mods Enterprises, LLC</p>
                      <p><span className="font-medium">Street Address:</span> {project?.address || 'N/A'}</p>
                      <p><span className="font-medium">City:</span> {project?.city || 'Cedar Rapids'}</p>
                    </div>
                  </div>
                    
                  {/* Editable Claimant Info */}
                  <div className="space-y-3">
                    <h5 className="font-medium text-slate-900 text-sm">Claimant Information</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Claimant Name / Company *</Label>
                        <Input
                          value={lienWaiverData.claimant_name}
                          onChange={(e) => setLienWaiverData(prev => ({ ...prev, claimant_name: e.target.value }))}
                          placeholder="Your company or name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Claimant Address</Label>
                        <Input
                          value={lienWaiverData.claimant_address}
                          onChange={(e) => setLienWaiverData(prev => ({ ...prev, claimant_address: e.target.value }))}
                          placeholder="Street, City, State, ZIP"
                          className="mt-1"
                        />
                      </div>
                      </div>
                    </div>
                    
                    {/* Waiver Details */}
                    <div className="space-y-3">
                      <h5 className="font-medium text-slate-900 text-sm">Waiver Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Work Description</Label>
                          <Input
                            value={lienWaiverData.work_description}
                            onChange={(e) => setLienWaiverData(prev => ({ ...prev, work_description: e.target.value }))}
                            placeholder="Type of work"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Amount ($) *</Label>
                          <Input
                            type="number"
                            value={lienWaiverData.waiver_amount}
                            onChange={(e) => setLienWaiverData(prev => ({ ...prev, waiver_amount: e.target.value }))}
                            placeholder="0.00"
                            step="0.01"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Through Date *</Label>
                          <Input
                            type="date"
                            value={lienWaiverData.through_date}
                            onChange={(e) => setLienWaiverData(prev => ({ ...prev, through_date: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Waiver Terms */}
                    <div className="p-3 bg-slate-100 rounded-lg text-xs text-slate-600 leading-relaxed">
                      <p className="mb-2">
                        The undersigned, for and in consideration of the payment of the above amount, receipt of which is hereby 
                        acknowledged, does hereby waive and release any and all lien or claim of lien on account of labor or materials 
                        furnished by the undersigned to or for the above described property through the date shown above.
                      </p>
                    </div>
                    
                    {/* Signature Section */}
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm font-medium text-purple-900 mb-3">Sign Below</p>
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        className="border-2 border-dashed border-purple-300 rounded-lg bg-white w-full touch-none cursor-crosshair"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={clearSignature}>
                          Clear
                        </Button>
                        <Button
                          size="sm"
                          onClick={submitESignature}
                          className="bg-purple-600 hover:bg-purple-700 ml-auto"
                          disabled={!lienWaiverData.claimant_name || !lienWaiverData.waiver_amount || isUploading.lienWaiver}
                        >
                          {isUploading.lienWaiver ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                          ) : (
                            'Submit Signed Waiver'
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-purple-600 mt-2">
                        By signing, you agree to the terms of this lien waiver document.
                      </p>
                    </div>
                  {/* End of Lien Waiver section */}
                </div>
              </CardContent>
            )}
          </Card>

          {/* 2. Completion Photos */}
          <Card className={phase?.completion_photos_uploaded ? 'border-green-200 bg-green-50/50' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    phase?.completion_photos_uploaded ? 'bg-green-100' : 'bg-purple-100'
                  }`}>
                    {phase?.completion_photos_uploaded ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Camera className="w-5 h-5 text-purple-600" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg">Completion Photos</CardTitle>
                    <CardDescription>
                      {phase?.completion_photos_uploaded 
                        ? `${phase.completion_photos?.length || 0} photo(s) uploaded` 
                        : 'Upload photos showing completed work'
                      }
                    </CardDescription>
                  </div>
                </div>
                {phase?.completion_photos_uploaded ? (
                  <Badge className="bg-green-100 text-green-700">Complete</Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">Required</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!phase?.completion_photos_uploaded && (
                  <Alert>
                    <Camera className="w-4 h-4" />
                    <AlertDescription>
                      Upload photos showing the completed work. These serve as proof of completion for project records.
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Existing photos */}
                {phase?.completion_photos?.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {phase.completion_photos.map((photo, idx) => (
                      <div key={photo.id || idx} className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                        <Camera className="w-8 h-8 text-slate-400" />
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload new photos */}
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6">
                  {completionPhotos.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {completionPhotos.map((photo, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
                            <Camera className="w-4 h-4 text-slate-500" />
                            <span className="text-sm text-slate-700">{photo.name}</span>
                            <button 
                              onClick={() => setCompletionPhotos(prev => prev.filter((_, i) => i !== idx))}
                              className="text-slate-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div>
                        <Label className="text-sm">Description (optional)</Label>
                        <Textarea
                          value={photoDescription}
                          onChange={(e) => setPhotoDescription(e.target.value)}
                          placeholder="Describe the completed work..."
                          className="mt-1"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Camera className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 mb-2">Upload completion photos</p>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setCompletionPhotos(Array.from(e.target.files || []))}
                        className="hidden"
                        id="completion-photos-upload"
                      />
                      <label htmlFor="completion-photos-upload">
                        <Button variant="outline" asChild>
                          <span><Plus className="w-4 h-4 mr-2" /> Select Photos</span>
                        </Button>
                      </label>
                      <p className="text-xs text-slate-500 mt-2">Select multiple photos</p>
                    </div>
                  )}
                </div>
                
                {completionPhotos.length > 0 && (
                  <Button 
                    onClick={handlePhotosUpload} 
                    disabled={isUploading.photos}
                    className="w-full"
                  >
                    {isUploading.photos ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Upload {completionPhotos.length} Photo(s)</>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 3. Inspection Report (if required) */}
          {phase?.requires_inspection && (
            <Card className={phase?.inspection_passed ? 'border-green-200 bg-green-50/50' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      phase?.inspection_passed ? 'bg-green-100' : 'bg-amber-100'
                    }`}>
                      {phase?.inspection_passed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <ClipboardCheck className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg">Inspection Report</CardTitle>
                      <CardDescription>
                        {phase?.inspection_passed 
                          ? 'Inspection report uploaded' 
                          : 'Upload passed city inspection report'
                        }
                      </CardDescription>
                    </div>
                  </div>
                  {phase?.inspection_passed ? (
                    <Badge className="bg-green-100 text-green-700">Complete</Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-600 border-amber-300">Required</Badge>
                  )}
                </div>
              </CardHeader>
              {!phase?.inspection_passed && (
                <CardContent>
                  <div className="space-y-4">
                    <Alert className="bg-amber-50 border-amber-200">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <AlertDescription className="text-amber-800">
                        This phase requires a city inspection. Please upload documentation showing the inspection has passed.
                      </AlertDescription>
                    </Alert>
                    
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                      {inspectionFile ? (
                        <div className="flex items-center justify-center gap-3">
                          <FileText className="w-8 h-8 text-amber-500" />
                          <div className="text-left">
                            <p className="font-medium text-slate-700">{inspectionFile.name}</p>
                            <p className="text-sm text-slate-500">{(inspectionFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => setInspectionFile(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <ClipboardCheck className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                          <p className="text-slate-600 mb-2">Upload inspection report</p>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setInspectionFile(e.target.files?.[0])}
                            className="hidden"
                            id="inspection-upload"
                          />
                          <label htmlFor="inspection-upload">
                            <Button variant="outline" asChild>
                              <span>Choose File</span>
                            </Button>
                          </label>
                          <p className="text-xs text-slate-500 mt-2">PDF, JPG, or PNG</p>
                        </>
                      )}
                    </div>
                    
                    {inspectionFile && (
                      <Button 
                        onClick={handleInspectionUpload} 
                        disabled={isUploading.inspection}
                        className="w-full"
                      >
                        {isUploading.inspection ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                        ) : (
                          <><Upload className="w-4 h-4 mr-2" /> Upload Inspection Report</>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* 4. W-9 Tax Form */}
          <Card className={`overflow-hidden transition-all duration-300 ${portalData?.contractor?.w9_received ? 'border-green-200 bg-green-50/30' : 'border-slate-200'}`}>
            <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${portalData?.contractor?.w9_received ? 'bg-green-100' : 'bg-slate-100'}`}>
                    <FileText className={`w-5 h-5 ${portalData?.contractor?.w9_received ? 'text-green-600' : 'text-slate-600'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">W-9 Tax Form</CardTitle>
                    <CardDescription>
                      {portalData?.contractor?.w9_received
                        ? 'W-9 on file' 
                        : 'Required for tax reporting'}
                    </CardDescription>
                  </div>
                </div>
                {portalData?.contractor?.w9_received && (
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> On File
                  </Badge>
                )}
              </div>
            </CardHeader>
            {!portalData?.contractor?.w9_received && (
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <Alert>
                    <FileText className="w-4 h-4" />
                    <AlertDescription>
                      Please upload your W-9 form for tax reporting purposes.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    {w9File ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileCheck className="w-5 h-5 text-green-600" />
                        <span className="text-slate-700">{w9File.name}</span>
                        <Button variant="ghost" size="sm" onClick={() => setW9File(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-600 mb-2">Upload your W-9 form</p>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => setW9File(e.target.files?.[0])}
                          className="hidden"
                          id="w9-upload"
                        />
                        <label htmlFor="w9-upload">
                          <Button variant="outline" asChild>
                            <span>Choose File</span>
                          </Button>
                        </label>
                        <p className="text-xs text-slate-500 mt-2">PDF, JPG, or PNG</p>
                      </>
                    )}
                  </div>
                  
                  {w9File && (
                    <Button 
                      onClick={handleW9Upload} 
                      disabled={isUploading.w9}
                      className="w-full"
                    >
                      {isUploading.w9 ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> Upload W-9</>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            )}
          </Card>

          {/* 5. Certificate of Insurance (COI) */}
          <Card className={`overflow-hidden transition-all duration-300 ${portalData?.contractor?.coi_received && !portalData?.contractor?.coi_expired ? 'border-green-200 bg-green-50/30' : portalData?.contractor?.coi_expired ? 'border-orange-200 bg-orange-50/30' : 'border-slate-200'}`}>
            <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${portalData?.contractor?.coi_received && !portalData?.contractor?.coi_expired ? 'bg-green-100' : portalData?.contractor?.coi_expired ? 'bg-orange-100' : 'bg-slate-100'}`}>
                    <Shield className={`w-5 h-5 ${portalData?.contractor?.coi_received && !portalData?.contractor?.coi_expired ? 'text-green-600' : portalData?.contractor?.coi_expired ? 'text-orange-600' : 'text-slate-600'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Certificate of Insurance</CardTitle>
                    <CardDescription>
                      {portalData?.contractor?.coi_received && !portalData?.contractor?.coi_expired
                        ? 'Insurance certificate on file' 
                        : portalData?.contractor?.coi_expired
                        ? 'Insurance certificate has expired'
                        : 'Required before payment'}
                    </CardDescription>
                  </div>
                </div>
                {portalData?.contractor?.coi_received && !portalData?.contractor?.coi_expired && (
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> On File
                  </Badge>
                )}
                {portalData?.contractor?.coi_expired && (
                  <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                    <AlertCircle className="w-3 h-3 mr-1" /> Expired
                  </Badge>
                )}
              </div>
            </CardHeader>
            {(!portalData?.contractor?.coi_received || portalData?.contractor?.coi_expired) && (
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <Alert className={portalData?.contractor?.coi_expired ? 'border-orange-200 bg-orange-50' : ''}>
                    <Shield className="w-4 h-4" />
                    <AlertDescription>
                      {portalData?.contractor?.coi_expired 
                        ? 'Your Certificate of Insurance has expired. Please upload a current COI.'
                        : 'Please upload your Certificate of Insurance (COI) showing current coverage.'}
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm">Expiration Date <span className="text-red-600">*</span></Label>
                      <Input
                        type="date"
                        value={coiExpiration}
                        onChange={(e) => setCoiExpiration(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        required
                        className="mt-1"
                        data-testid="coi-expiration-input-portal"
                      />
                      <p className="text-xs text-slate-500 mt-1">Required — used to alert the project owner before coverage lapses.</p>
                    </div>
                  </div>
                  
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    {coiFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileCheck className="w-5 h-5 text-green-600" />
                        <span className="text-slate-700">{coiFile.name}</span>
                        <Button variant="ghost" size="sm" onClick={() => setCoiFile(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-600 mb-2">Upload Certificate of Insurance</p>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => setCoiFile(e.target.files?.[0])}
                          className="hidden"
                          id="coi-upload"
                        />
                        <label htmlFor="coi-upload">
                          <Button variant="outline" asChild>
                            <span>Choose File</span>
                          </Button>
                        </label>
                        <p className="text-xs text-slate-500 mt-2">PDF, JPG, or PNG</p>
                      </>
                    )}
                  </div>
                  
                  {coiFile && (
                    <Button 
                      onClick={handleCOIUpload} 
                      disabled={isUploading.coi}
                      className="w-full"
                    >
                      {isUploading.coi ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> Upload Certificate of Insurance</>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>Questions? Contact the project owner for assistance.</p>
          <p className="mt-2">Powered by <span className="font-semibold text-blue-600">DrawReady</span></p>
        </div>
      </div>
    </div>
  );
}

export default ContractorCompliancePortal;
